import React from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Button,
  Divider,
  TextField,
} from "@mui/material";
import RulesPanel from "../components/RulesPanel";

const DevIdeEditorPanel = ({
  selectedFileId,
  selectedFileMeta,
  fileContent,
  fileOriginalContent, // kept for future if needed
  fileLoading,
  fileError,
  gptPasteContent,
  changeSummary,
  saveLoading,
  saveMessage,
  diffText,
  diffLoading,
  diffError,
  diffLineCount,
  onCopyFullForGPT,
  onCopySelectionForGPT,
  onChangeFileContent,
  onChangeGptPasteContent,
  onApplyGptPaste,
  onChangeSummary,
  onPreviewDiff,
  onSaveFile,

  // NEW: revert
  onRevertFile,

  // NEW: 3 callbacks
  onRunBackendScan, // NEW
  onRunFrontendScan, // NEW
  onDescribeFile, // NEW

  // NEW: active change context
  activeChange, // NEW

  // NEW: file history props
  onLoadFileHistory, // NEW
  fileHistory, // NEW
  fileHistoryLoading, // NEW
  fileHistoryError, // NEW

  // NEW: rules props (CHG-037)
  rulesMarkdown,
  rulesLoading,
  rulesError,
}) => {
  if (!selectedFileId) {
    return (
      <Box p={3}>
        <Typography variant="body1">
          Select a file on the left to view and edit.
        </Typography>
      </Box>
    );
  }

  const handleLoadAllHistory = () => {
    if (onLoadFileHistory) {
      onLoadFileHistory("ALL");
    }
  };

  const handleLoadActiveChangeHistory = () => {
    if (onLoadFileHistory && activeChange?.id) {
      onLoadFileHistory("ACTIVE_CHANGE_ONLY");
    }
  };

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box p={2} borderBottom="1px solid rgba(0,0,0,0.12)">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="subtitle1">
              {selectedFileMeta?.path || "Loading..."}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Module: {selectedFileMeta?.module || "-"} - Lines:{" "}
              {selectedFileMeta?.line_count ?? ""}
            </Typography>

            {selectedFileMeta?.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                {selectedFileMeta.description}
              </Typography>
            )}

            {/* NEW: show active change context */}
            {activeChange && (
              <Typography
                variant="caption"
                color="primary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Active change: #{activeChange.id} {activeChange.title}
                {activeChange.status ? ` [${activeChange.status}]` : ""}
              </Typography>
            )}
          </Box>

          {selectedFileMeta && !selectedFileMeta.is_patch_friendly && (
            <Chip
              label="Large / monolithic - be cautious"
              color="warning"
              size="small"
            />
          )}
        </Stack>
      </Box>

      {/* Main area: editor + rules side panel */}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Left: editor + history */}
        <Box
          p={2}
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{ flex: 2, minHeight: 0 }}
        >
          {fileLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <CircularProgress size={24} />
            </Box>
          )}

          {fileError && (
            <Typography color="error" variant="body2">
              {fileError}
            </Typography>
          )}

          {!fileLoading && !fileError && (
            <>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onCopyFullForGPT}
                >
                  Copy full file for GPT
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onCopySelectionForGPT}
                >
                  Copy (selection) for GPT
                </Button>

                {/* NEW BUTTONS */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onRunBackendScan}
                >
                  Run Backend Scan
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onRunFrontendScan}
                >
                  Run Frontend Scan
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onDescribeFile}
                >
                  Update Description
                </Button>
              </Stack>

              <TextField
                label="File content (sandbox)"
                multiline
                fullWidth
                value={fileContent}
                onChange={(e) => onChangeFileContent(e.target.value)}
                minRows={16}
                maxRows={24}
                sx={{ fontFamily: "monospace", flexGrow: 1 }}
              />

              <Divider />

              <Typography variant="subtitle2">
                Paste updated file from GPT (optional)
              </Typography>
              <TextField
                label="Paste full updated file from GPT here"
                multiline
                fullWidth
                value={gptPasteContent}
                onChange={(e) => onChangeGptPasteContent(e.target.value)}
                minRows={6}
                sx={{ fontFamily: "monospace" }}
              />
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onApplyGptPaste}
                  sx={{ mt: 1 }}
                >
                  Apply GPT content to editor
                </Button>
              </Box>

              <Divider />

              <TextField
                label="Change summary (required)"
                fullWidth
                value={changeSummary}
                onChange={(e) => onChangeSummary(e.target.value)}
                placeholder="e.g. 'Update PO total when adding purchase requests'"
              />

              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mt={1}
                gap={1}
                flexWrap="wrap"
              >
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onPreviewDiff}
                    disabled={diffLoading}
                  >
                    {diffLoading
                      ? "Generating diff..."
                      : "Preview diff vs loaded file"}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={onSaveFile}
                    disabled={saveLoading}
                  >
                    {saveLoading
                      ? "Saving..."
                      : "Save to sandbox (with backup)"}
                  </Button>
                </Stack>

                {saveMessage && (
                  <Typography
                    variant="caption"
                    color={
                      saveMessage.toLowerCase().includes("error")
                        ? "error"
                        : "primary"
                    }
                  >
                    {saveMessage}
                  </Typography>
                )}
              </Box>

              <Box mt={2}>
                <Typography variant="subtitle2">Diff preview</Typography>
                {diffError && (
                  <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    {diffError}
                  </Typography>
                )}
                {diffLineCount !== null && !diffError && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Changed lines (excluding headers): {diffLineCount}
                  </Typography>
                )}
                <TextField
                  label="Unified diff (read-only)"
                  multiline
                  fullWidth
                  value={diffText}
                  InputProps={{ readOnly: true }}
                  minRows={6}
                  sx={{ fontFamily: "monospace" }}
                />
              </Box>

              {/* File history section */}
              <Box mt={2}>
                <Typography variant="subtitle2">File history</Typography>

                <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleLoadAllHistory}
                    disabled={fileHistoryLoading || !onLoadFileHistory}
                  >
                    {fileHistoryLoading ? "Loading..." : "Load history"}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleLoadActiveChangeHistory}
                    disabled={
                      fileHistoryLoading ||
                      !onLoadFileHistory ||
                      !activeChange?.id
                    }
                  >
                    {fileHistoryLoading
                      ? "Loading..."
                      : "History for active change"}
                  </Button>
                </Stack>

                {fileHistoryError && (
                  <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    {fileHistoryError}
                  </Typography>
                )}

                {!fileHistoryLoading &&
                  !fileHistoryError &&
                  (!fileHistory || fileHistory.length === 0) && (
                    <Typography variant="caption" color="text.secondary">
                      No history entries loaded yet.
                    </Typography>
                  )}

                {fileHistory && fileHistory.length > 0 && (
                  <Box
                    sx={{
                      maxHeight: 160,
                      overflowY: "auto",
                      border: "1px solid rgba(0,0,0,0.12)",
                      borderRadius: 1,
                      p: 1,
                      mt: 1,
                    }}
                  >
                    {fileHistory.map((h) => (
                      <Box key={h.id} sx={{ mb: 0.75 }}>
                        <Typography variant="caption" color="text.secondary">
                          {h.saved_at} — change #{h.change_id || "?"} —{" "}
                          {h.username || "?"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ display: "block" }}
                        >
                          {h.save_summary || "(no summary)"} — backup:{" "}
                          {h.backup_path || "n/a"}
                        </Typography>
                        {h.backup_path && onRevertFile && (
                          <Box sx={{ mt: 0.5 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => onRevertFile(selectedFileId, h.backup_path)}
                            >
                              Revert
                            </Button>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Right: rules panel */}
        <Box
          sx={{
            width: 320,
            maxWidth: "35%",
            minWidth: 240,
            borderLeft: "1px solid rgba(0,0,0,0.12)",
            p: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <RulesPanel
            rulesMarkdown={rulesMarkdown}
            loading={rulesLoading}
            error={rulesError}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DevIdeEditorPanel;
